import { useQuery } from "@apollo/client";
import React, { useEffect, useState } from "react";
import { RouteComponentProps, useHistory, useLocation } from "react-router-dom";

import { PageNotFound, StyledGrid } from "components/simple";
import { UserAssessmentCycleStatus } from "components/simple/CycleSummary/types";
import { GetAppropriateAssessmentCycleOverview } from "graphql/admin/assessments";
import { GetUserAssessmentCycleIdsAndNumbers } from "graphql/bhi/cycles.gql";
import { useDispatch } from "hooks";
import { color, spacingDefaults } from "style/constants";
import { AssessmentType, Pillar } from "types";
import AceScores from "./AceScores";
import CycleHeader from "./CycleHeader";
import GroupHeader from "./GroupHeader";
import SingleAssessment from "./SingleAssessment";
import TotalScoreHeader from "./TotalScoreHeader";
import {
  GetAppropriateAssessmentCycleOverviewData,
  GetUserAssessmentCycleIdsAndNumbersData
} from "./types";
import BrainHQAssessment from "./BrainHQAssessment";

interface MatchParams {
  id: string;
}
type Props = RouteComponentProps<MatchParams, {}>;

export function getDisplayPillarName(name: string): string {
  // There is a discrepancy between the pillar and group names
  switch (name) {
    case "clarity":
      return "Clarity";
    case "cognitive":
      return "Cognition";
    case "connectedness":
      return "Connectedness";
    case "emerging":
      return "Emerging Factor";
    case "emotionalBalance":
      return "Emotional Balance";
    case "index":
    case "newIndex":
      return "Your BrainHealth® Index";
    case "realLife":
      return "Daily Life";
    case "social":
      return "Social";
    case "wellBeing":
      return "Well-Being";
    default:
      return name;
  }
}

const UserAssessments: React.FC<Props> = ({ match, location }: Props): JSX.Element => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();
  const nextAssesmentIds: any = {};
  const [refactor, setRefactor] = useState(search.slice(0, search.length).includes("refactor"));
  const [userId, setUserId] = useState<string>("");
  const [cycles, setCycles] = useState<GetUserAssessmentCycleIdsAndNumbersData>();
  const [pillars, setPillars] = useState<string[]>([]);
  const [userAssessmentCycleData, setUserAssessmentCycleData] = useState<
    GetAppropriateAssessmentCycleOverviewData
  >();
  const [isCodingClinicianReviewed, setIsCodingClinicianReviewed] = useState<boolean>(false);
  const userAssessmentCycleId = match.params.id;
  const { data: cycleData, loading, error, refetch } = useQuery<
    GetAppropriateAssessmentCycleOverviewData
  >(GetAppropriateAssessmentCycleOverview, {
    variables: {
      id: userAssessmentCycleId,
      refactor
    },
    fetchPolicy: "no-cache"
  });

  // query to populate cycle dropdown
  const { error: cycleDataError, loading: cycleDataLoading } = useQuery<
    GetUserAssessmentCycleIdsAndNumbersData
  >(GetUserAssessmentCycleIdsAndNumbers, {
    skip: !userId,
    variables: {
      filters: [
        {
          filterColumn: "user_assessment_cycles.user_id",
          filterValue: userId
        }
      ],
      sort: { column: "cycle_number", order: "asc" }
    },
    onCompleted: data => {
      const sortedCycles = data.userAssessmentCycles.data.filter(cycle => {
        return cycle.status != UserAssessmentCycleStatus.NOT_STARTED;
      });
      setCycles({
        userAssessmentCycles: {
          data: sortedCycles
        }
      });
    },
    onError: () => dispatch({ type: "error" })
  });

  // if refactor is true we need use "bhiRefactorPillarScores", otherwise use "pillarScores"
  useEffect(() => {
    if (cycleData?.userAssessmentCycle) {
      let pillars: string[] = [];
      if (refactor) {
        const updatedCycleData = {
          ...cycleData,
          userAssessmentCycle: {
            ...cycleData.userAssessmentCycle,
            pillarScores: cycleData.userAssessmentCycle.bhiRefactorPillarScores
          }
        };
        setUserAssessmentCycleData(updatedCycleData as GetAppropriateAssessmentCycleOverviewData);
        pillars = cycleData.userAssessmentCycle.bhiRefactorPillars;
      } else {
        setUserAssessmentCycleData(cycleData);
        pillars = cycleData.userAssessmentCycle.pillars;
      }
      pillars = [...pillars, "About You"]; // report has About You section at bottom
      setPillars(pillars);
      setUserId(cycleData.userAssessmentCycle.user.id);
      const isCodingClinicianReviewed = cycleData.userAssessmentCycle.userAssessments.find(
        assessment => assessment.name === "Coding"
      )?.isCodingClinicianReviewed;
      setIsCodingClinicianReviewed(isCodingClinicianReviewed!);
    }
  }, [cycleData, refactor]);

  useEffect(() => {
    setRefactor(search.slice(0, search.length).includes("refactor"));
  }, [search]);

  if (loading || cycleDataLoading) return <></>;
  if (
    error ||
    cycleDataError ||
    !userAssessmentCycleData ||
    !cycles ||
    !userAssessmentCycleData.userAssessmentCycle ||
    !userAssessmentCycleData.userAssessmentCycle.pillarScores
  ) {
    return <PageNotFound />;
  }
  const status = userAssessmentCycleData.userAssessmentCycle.status;
  const indexPillarName = refactor ? "newIndex" : "index";

  function getPillarScore(name: string): number | null {
    if (!userAssessmentCycleData?.userAssessmentCycle.pillarScores) return null;

    const pillar = userAssessmentCycleData.userAssessmentCycle.pillarScores.find(
      (pillar: Pillar) => pillar.pillarName === name
    );

    if (pillar) return pillar.value;
    return null;
  }

  function getBackendPillarName(name: string): string {
    // There is a discrepancy between the pillar and group names
    switch (name) {
      case "Cognition":
        return "cognitive";
      case "Well-Being":
        return "wellBeing";
      case "Daily Life":
        return "realLife";
      case "Social":
        return "social";
      default:
        return name;
    }
  }

  function handleCycleChanged(selectedCycle: number) {
    const url = `/user-assessments/${cycles?.userAssessmentCycles.data[selectedCycle].id}${
      refactor ? "?refactor" : ""
    }`;
    history.push(url);
    history.go(0);
  }

  return (
    <>
      <CycleHeader
        userAssessmentCycle={userAssessmentCycleData.userAssessmentCycle}
        cycleNumbers={cycles?.userAssessmentCycles.data.map(cycle => cycle.cycleNumber)}
        refetch={refetch}
        handleCycleChanged={handleCycleChanged}
        isCodingClinicianReviewed={isCodingClinicianReviewed}
        refactor={refactor}
        hasInvalidAssessments={userAssessmentCycleData.userAssessmentCycle.needsValidityReview}
      />

      <StyledGrid container fillRemainingHeight margin="auto" maxWidth="1048px" width="75%">
        <StyledGrid
          width="100%"
          margin={`${spacingDefaults.large} auto`}
          backgroundColor={color.WHITE}
          border={`solid 1px ${color.MUTEDGRAY}`}
          borderRadius="8px"
        >
          <TotalScoreHeader
            score={getPillarScore(indexPillarName)}
            status={status}
            readyToFinalize={userAssessmentCycleData.userAssessmentCycle.readyToFinalize}
            generalNote={userAssessmentCycleData.userAssessmentCycle.internalNotes?.length}
          />
          {pillars.map(p => {
            const userAssessmentsForPillar = userAssessmentCycleData.userAssessmentCycle.userAssessments.filter(
              ua =>
                refactor
                  ? ua.assessment.pillarAssessments.some(pa => pa.pillarName === p)
                  : getBackendPillarName(ua.assessment.assessmentGrouping.name) === p
            );
            userAssessmentsForPillar.sort(
              (a: AssessmentType, b: AssessmentType) =>
                a.assessment.assessmentGrouping.assessmentPart.order -
                  b.assessment.assessmentGrouping.assessmentPart.order ||
                a.assessment.assessmentGrouping.order - b.assessment.assessmentGrouping.order ||
                a.assessment.order - b.assessment.order
            );
            userAssessmentsForPillar.map((currAssessment, idx) => {
              nextAssesmentIds[currAssessment.id] =
                idx < userAssessmentsForPillar.length - 1 &&
                !userAssessmentsForPillar[idx + 1].assessment.automatedScoring
                  ? userAssessmentsForPillar[idx + 1].id
                  : "";
            });
            return (
              <>
                <GroupHeader
                  key={p}
                  name={getDisplayPillarName(p)}
                  pillarScore={getPillarScore(p)}
                />
                {userAssessmentsForPillar.map((userAssessment, idx) => {
                  const isAutomatedScored = userAssessment.assessment.automatedScoring;
                  const assessmentName = userAssessment.assessment.displayName;
                  const scoreRanges = userAssessment.scoreRanges;

                  // strategy has a special scoring system, the assessment score is named "Aggregate"
                  // also includes the individual question raw scores (OBPI-1B, OBPI-1D, and OBPI-1F)
                  const isStrategy = assessmentName === "Strategy";
                  const scores = isStrategy
                    ? userAssessment.scores.filter(
                        (s: { scoreName: string }) => s.scoreName === "Aggregate"
                      )
                    : userAssessment.scores;
                  return (
                    <SingleAssessment
                      id={userAssessment.id}
                      key={userAssessment.id}
                      name={assessmentName}
                      notes={
                        userAssessment.internalNotes?.length
                          ? userAssessment.internalNotes.length
                          : undefined
                      }
                      scores={scores}
                      scoreRanges={scoreRanges}
                      automatedScored={isAutomatedScored}
                      status={status}
                      isClaimedByCurrentUser={
                        userAssessmentCycleData?.userAssessmentCycle.isClaimedByCurrentUser || false
                      }
                      nextAssesmentIds={nextAssesmentIds}
                      validityCode={userAssessment.validityCode}
                    />
                  );
                })}
              </>
            );
          })}
        </StyledGrid>
        <StyledGrid item marginBottom={spacingDefaults.large} xs={12}>
          {/* <AceScores aceScores={userAssessmentCycleData.userAssessmentCycle.aceScores} /> */}
          <BrainHQAssessment
            score={
              userAssessmentCycleData.userAssessmentCycle.userBrainHqAssessment?.summaryStatistics
            }
          />
        </StyledGrid>
      </StyledGrid>
    </>
  );
};

export default UserAssessments;
